import {Injectable, Type} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {MvsCrudModeEnum} from "../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {CrSepaMandateCaptureRequestDto} from "../../model/dto/api/cr-sepa-mandate-capture-request.dto";
import {CrSepaMandateDocumentRequestDto} from "../../model/dto/api/cr-sepa-mandate-document-request.dto";
import {CrSepaMandateStateDto} from "../../model/dto/api/cr-sepa-mandate-state.dto";
import {
    CrCustomerBankAccountCreateObjectComponent
} from "../../component/object-components/cr-customer-bank-account-create-object-component/cr-customer-bank-account-create-object.component";
import {
    CrCustomerBankAccountChangeObjectComponent
} from "../../component/object-components/cr-customer-bank-account-change-object-component/cr-customer-bank-account-change-object.component";
// TM-1000002030: the read-only overview OBC, selected by viewType
import {
    CrCustomerBankAccountOverviewObjectComponent
} from '../../component/object-components/cr-customer-bank-account-overview-object-component/cr-customer-bank-account-overview-object.component';
import {CR_BANK_ACCOUNT_VIEW_TYPE_OVERVIEW} from '../../logic/cr-bank-account-view-type';

/**
 * Data-layer service of {@code cr.CustomerBankAccount}, extended with the five SEPA mandate action
 * endpoints (projects/sepa_storage §4). Standard CRUD keeps riding the generic
 * {@code ObjectCrudController} the entity-provider route already covers; only the mandate actions
 * need dedicated methods, which is why this service exists at all (see
 * {@code fe_core_mvs-crud-service} — a bare CRUD wrapper would be an anti-pattern).
 * <p>
 * ALL FIVE branches go through an action, the plain capture ({@code withoutDocument}) included. It
 * was once written as an ordinary CRUD write of signature date and reason, on the assumption that
 * {@code CustomerBankAccountOnChange} would raise the status. It does not and cannot: the hook
 * DERIVES a status where none is given, it never RAISES one, so that write left the account on
 * {@code none} with no mandate reference and no visibility to the pain.008. Sending the status in
 * the payload instead is forbidden by R-28. See {@link #captureMandate}.
 */
@Injectable({
    providedIn: 'root'
})
export class CustomerBankAccountService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cr/customerBankAccounts');
    }

    /**
     * The bank account OBCs: create and change (R-31), plus the read-only OVERVIEW selected by
     * `viewType` (TM-1000002030).
     *
     * Resolution (create always wins, an unknown viewType behaves like none):
     *
     * | mode               | viewType     | result                                          |
     * |--------------------|--------------|-------------------------------------------------|
     * | create             | any          | `CrCustomerBankAccountCreateObjectComponent`    |
     * | read/update/null   | `'overview'` | `CrCustomerBankAccountOverviewObjectComponent`  |
     * | read               | none/unknown | `null` (generic CRUD form)                      |
     * | update/null        | none/unknown | `CrCustomerBankAccountChangeObjectComponent`    |
     *
     * Registered through the SERVICE override rather than through the entity-provider
     * `objectComponent` array. The entity-provider entry of cr.CustomerBankAccount carries `route`
     * and `service` but deliberately NO `objectComponent` array. The framework resolves the
     * entity-provider registry FIRST and falls back to this override WITH the viewType
     * (`MvsCoreService.getObjectComponent`), so registering here and not there is the one
     * consistent way to do it; registering in BOTH places is what must be avoided. A viewType-only
     * registry mapping would also match `create`, which is why the viewType branch lives here.
     * <p>
     * There is no circular import: the OBCs reach cr.CustomerBankAccount through
     * {@code MvsCoreService}, and the mandate endpoints of this service are consumed by
     * {@code cr-sepa-mandate-capture}, which no OBC imports in TypeScript - it is embedded by
     * selector only.
     * <p>
     * READ WITHOUT viewType RETURNS NULL ON PURPOSE, AND THAT IS NOT AN OMISSION. This service sits
     * in the global entity registry, so every override here answers for the WHOLE application. The
     * change OBC is an EDITING surface (editable {@code mvs-crud-object}, SEPA mandate block with
     * its capture actions); handing it out for {@code read} would turn every read-only open of a
     * bank account into an editable mask. Null lets the caller fall through to the generic CRUD form
     * ({@code object.component.ts} - no component means {@code useCrudComponent}). What that form
     * permits is the CALLER's decision, carried by the {@code formControlOverwrite} it passes down.
     * The overview, in contrast, is read-only by construction (`[disableUpdate]`, display-only
     * widget params), so it may answer {@code read} too.
     * <p>
     * {@code mode = null} (passed by {@code object-page.component.ts}) intentionally lands on the
     * change OBC when no viewType is given: an object page is an editing context, and a default
     * parameter does not fire for null.
     * <p>
     * HANDOVER TO TM-1000002027 (Abrechnungs-Landkarte): the overview is reached ONLY with
     * `viewType = CR_BANK_ACCOUNT_VIEW_TYPE_OVERVIEW`. Build the entry with
     * `crBuildBankAccountOverviewNavigationEntry(id, label)` (cr/logic/cr-bank-account-view-type.ts)
     * and open it with `navigationService.navigateTo(entry, 'right')`. NEVER use
     * `MvsUiObjectService.openObjectViaDialog` for it: that route cannot carry a viewType and
     * would open the change OBC. Opens without a viewType (edit dialogs, cw step Zahlung, account
     * tab, generic object page, pop-out, new tab) keep the change OBC on purpose; the new-tab
     * and generic-page routes drop the viewType in core.
     *
     * @param mode the CRUD mode the caller opened the object in
     * @param viewType `'overview'` selects the read-only overview for every mode except create; any
     *                 other value (or none) leaves the behaviour as before
     * @ticket TM-1000002030
     */
    override getObjectComponent(
        mode: MvsCrudModeEnum = MvsCrudModeEnum.update,
        viewType?: any): Type<any> {

        if (mode === MvsCrudModeEnum.create) {
            return CrCustomerBankAccountCreateObjectComponent;
        }

        // TM-1000002030: viewType 'overview' selects the read-only overview (create has already won above)
        if (viewType === CR_BANK_ACCOUNT_VIEW_TYPE_OVERVIEW) {
            return CrCustomerBankAccountOverviewObjectComponent;
        }

        if (mode === MvsCrudModeEnum.read) {
            return null;
        }

        return CrCustomerBankAccountChangeObjectComponent;
    }

    /**
     * Capture a mandate granted without a supporting document and activate it (capture types
     * {@code withoutDocument} and {@code withoutDocumentNoReason}, R-33 / §3.1).
     *
     * ONE WRITE, not a CRUD save followed by an action. The request carries the chosen mandate
     * source alongside the two capture attributes, so the source pick and the capture cannot be
     * observed apart — the previous two-step shape let the capture run against an account whose
     * source PUT had not landed yet.
     *
     * @param bankAccountId the {@code cr.CustomerBankAccount} id
     * @param request signature date, missing-document reason and, optionally, the mandate source
     */
    public captureMandate(
        bankAccountId: number,
        request: CrSepaMandateCaptureRequestDto): Observable<CrSepaMandateStateDto> {

        const url = `${this.apiUrl}/${bankAccountId}/sepaMandate/capture`;
        return this.http.post<CrSepaMandateStateDto>(url, request);
    }

    /**
     * Book the result of an already-completed upload (capture type {@code withDocument}): attaches
     * the {@code dm.DmDocument} produced by {@code dm-document-upload} to the mandate.
     * @param bankAccountId the {@code cr.CustomerBankAccount} id
     * @param request the uploaded document id and the signature date
     */
    public attachMandateDocument(
        bankAccountId: number,
        request: CrSepaMandateDocumentRequestDto): Observable<CrSepaMandateStateDto> {

        const url = `${this.apiUrl}/${bankAccountId}/sepaMandate/document`;
        return this.http.post<CrSepaMandateStateDto>(url, request);
    }

    /**
     * Generate the mandate document from the source's signature template and start the sm signing
     * process (capture type {@code withSmProcess}).
     * @param bankAccountId the {@code cr.CustomerBankAccount} id
     */
    public startMandateSignature(bankAccountId: number): Observable<CrSepaMandateStateDto> {

        const url = `${this.apiUrl}/${bankAccountId}/sepaMandate/startSignature`;
        return this.http.post<CrSepaMandateStateDto>(url, null);
    }

    /**
     * Read the current mandate status for the OBC / {@code cr-sepa-mandate-capture}: status, process
     * id, signer roles, document id.
     * @param bankAccountId the {@code cr.CustomerBankAccount} id
     */
    public getMandateState(bankAccountId: number): Observable<CrSepaMandateStateDto> {

        const url = `${this.apiUrl}/${bankAccountId}/sepaMandate/state`;
        return this.http.get<CrSepaMandateStateDto>(url);
    }

    /**
     * Revoke the mandate: sets {@code sepaMandateRevokedDate} and status {@code revoked}.
     * @param bankAccountId the {@code cr.CustomerBankAccount} id
     */
    public revokeMandate(bankAccountId: number): Observable<CrSepaMandateStateDto> {

        const url = `${this.apiUrl}/${bankAccountId}/sepaMandate/revoke`;
        return this.http.post<CrSepaMandateStateDto>(url, null);
    }
}
